import { useContext, useEffect, useMemo, useState } from 'react';
import { Card, Col, Row, Spinner } from 'react-bootstrap';
import { useParams, useSearchParams } from 'react-router-dom';

import axios from 'axios';

import { apiFactory } from '../../../api/ApiFactory';
import { subscriptionsApi } from '../../../api/subscriptions';
import { toast } from '../../../utils/toast';
import { ApplicationContext } from '../../../contexts/ApplicationContext';
import { FbPixelEvents, useFbPixel } from '../../../hooks/FbPixel';
import {
	PIX_CHARGE_STORAGE_PREFIX,
	PIX_CONSENT_STORAGE_PREFIX,
	PixCharge
} from '../components/constants/pixCharge';
import { resolveSubscriptionRedirect } from '../components/helpers/resolveSubscriptionRedirect';

/** Vindi's `bill_paid` arrived. */
const ACTIVE_STATUS_ID = 3;

/** Consent rejected or expired: the sign-up will never complete. */
const EXPIRED_STATUS_ID = 9;

const POLL_INTERVAL_MS = 5000;

type IParams = { reference: string; platform?: string };

// Keeps a buyer out of the onboarding before paying.
export const RecurringPixCheckout = () => {
	const { reference, platform } = useParams<keyof IParams>() as IParams;
	const [searchParams] = useSearchParams();
	const { customization } = useContext(ApplicationContext);
	const { track } = useFbPixel();

	const [isConfirmed, setIsConfirmed] = useState(false);
	const [isExpired, setIsExpired] = useState(false);
	const [isApiKeyReady, setIsApiKeyReady] = useState(false);

	// Pix Automático has no QR code: the payer opens the consent link and
	// authorizes the recurring debit in their bank app.
	const isAutomatic = searchParams.get('pix_mode') === 'automatic';

	const consentUrl = useMemo(() => {
		try {
			return sessionStorage.getItem(
				`${PIX_CONSENT_STORAGE_PREFIX}${reference}`
			);
		} catch {
			return null;
		}
	}, [reference]);

	// Recurring resolves the api_key from the platform slug and drops it on
	// unmount (`enableDefault`), so this screen has to resolve it again before
	// polling — otherwise the request goes without the header and gets a 401.
	useEffect(() => {
		apiFactory
			.isDefaultApiKeyDefined()
			.then(async (isDefined) => {
				if (isDefined || !platform) return;

				const { data } = await axios.get<{ key: string }>(
					`${process.env.REACT_APP_CURSEDUCA_APPLICATION_DOMAIN}/platforms/slug/${platform}`
				);

				apiFactory.enableCustom(data.key);
			})
			.finally(() => setIsApiKeyReady(true));

		return () => apiFactory.enableDefault();
	}, [platform]);

	const charge = useMemo<PixCharge | null>(() => {
		try {
			const raw = sessionStorage.getItem(
				`${PIX_CHARGE_STORAGE_PREFIX}${reference}`
			);
			return raw ? (JSON.parse(raw) as PixCharge) : null;
		} catch {
			return null;
		}
	}, [reference]);

	useEffect(() => {
		if (isConfirmed || isExpired || !isApiKeyReady) return;

		let cancelled = false;

		const check = async () => {
			try {
				const subscription = await subscriptionsApi.findByReference(
					reference
				);

				if (cancelled) return;

				if (subscription.statusId === EXPIRED_STATUS_ID) {
					setIsExpired(true);
					return;
				}

				if (subscription.statusId !== ACTIVE_STATUS_ID) return;

				setIsConfirmed(true);
			} catch {
				// Keep polling: the buyer may be paying right now.
			}
		};

		check();
		const interval = setInterval(check, POLL_INTERVAL_MS);

		return () => {
			cancelled = true;
			clearInterval(interval);
		};
	}, [reference, isConfirmed, isExpired, isApiKeyReady]);

	useEffect(() => {
		if (!isConfirmed) return;

		const value = Number(searchParams.get('value')) || 0;

		track(FbPixelEvents.Purchase, { currency: 'BRL', value });

		const redirect = searchParams.get('redirect');

		if (redirect) {
			location.replace(redirect);
			return;
		}

		const planReference = searchParams.get('plan_reference') || '';

		const { url, isOnboarding } = resolveSubscriptionRedirect({
			planReference,
			receipt: reference,
			value,
			accessToken: localStorage.getItem('access_token') || '',
			platformUrl: customization?.url,
			isComercial:
				searchParams.get('origin')?.toUpperCase() === 'COMERCIAL',
			sellerToken: searchParams.get('token')
		});

		toast.success(
			isOnboarding
				? 'Pagamento confirmado! Redirecionando para o onboarding...'
				: 'Pagamento confirmado!'
		);

		setTimeout(() => location.replace(url), 2000);
	}, [isConfirmed]);

	const copyPixCode = async () => {
		if (!charge?.qrCode) return;

		try {
			await navigator.clipboard.writeText(charge.qrCode);
			toast.success('Código Pix copiado!');
		} catch {
			toast.error('Não foi possível copiar o código.');
		}
	};

	return (
		<div className="d-flex flex-column align-items-center">
			<div className="content">
				<div className="container">
					<Card className="rounded-xl p-md-10 my-10">
						<Card.Body className="text-center">
							{isExpired ? (
								<>
									<h3 className="h4 text-gray-800">
										A autorização não foi concluída
									</h3>
									<p className="text-gray-600">
										O prazo para autorizar expirou ou a
										solicitação foi recusada no aplicativo
										do banco. Nenhum valor foi cobrado.
									</p>
									<a
										href={`${customization?.url || ''}`}
										className="btn btn-primary mt-3"
									>
										Tentar novamente
									</a>
								</>
							) : isConfirmed ? (
								<>
									<h3 className="h4 text-gray-800">
										Pagamento confirmado!
									</h3>
									<p className="text-gray-600">
										Estamos preparando seu acesso...
									</p>
									<Spinner animation="border" />
								</>
							) : (
								<>
									<h3 className="h4 text-gray-800">
										{isAutomatic
											? 'Autorize no app do seu banco'
											: 'Falta pagar o Pix'}
									</h3>
									<p className="text-gray-600">
										{isAutomatic
											? 'Enviamos uma solicitação de autorização para o aplicativo do seu banco. Aprove por lá e sua assinatura é liberada — esta página atualiza sozinha.'
											: 'Sua assinatura é liberada assim que o pagamento for confirmado. Esta página atualiza sozinha — pode deixá-la aberta.'}
									</p>

									{charge?.qrCodeImage && (
										<Row className="justify-content-center my-5">
											<Col xs="auto">
												<div
													className="bg-white p-4 rounded d-inline-block"
													style={{
														border: '1px solid #ebedf3'
													}}
												>
													<img
														src={charge.qrCodeImage}
														alt="QR code do Pix"
														// pixelated: the Vindi QR
														// is a low-res PNG and
														// smoothing it while
														// scaling up breaks
														// readers.
														style={{
															width: '20rem',
															maxWidth: '100%',
															imageRendering:
																'pixelated'
														}}
													/>
												</div>
											</Col>
										</Row>
									)}

									{charge?.qrCode && (
										<button
											type="button"
											className="btn btn-outline-primary mb-3"
											onClick={copyPixCode}
										>
											Copiar código Pix
										</button>
									)}

									{consentUrl && (
										<>
											<div>
												<a
													href={consentUrl}
													target="_blank"
													rel="noopener noreferrer"
													className="btn btn-primary btn-lg"
												>
													Autorizar no meu banco
												</a>
											</div>
											<p className="text-gray-600 font-size-sm mt-4 mb-0">
												Você tem 60 minutos para
												concluir a autorização.
											</p>
										</>
									)}

									{charge?.paymentUrl && (
										<div>
											<a
												href={charge.paymentUrl}
												target="_blank"
												rel="noopener noreferrer"
												className="btn btn-primary"
											>
												Abrir página de pagamento
											</a>
										</div>
									)}

									{!charge && !consentUrl && !isAutomatic && (
										<p className="text-gray-600">
											Enviamos a cobrança para o seu
											e-mail. Assim que o pagamento for
											identificado, esta página segue
											automaticamente.
										</p>
									)}

									<div className="mt-8 d-flex align-items-center justify-content-center">
										<Spinner
											animation="border"
											size="sm"
											className="mr-3"
										/>
										<span className="text-gray-600">
											{isAutomatic
												? 'Aguardando sua autorização'
												: 'Aguardando confirmação do pagamento'}
										</span>
									</div>
								</>
							)}
						</Card.Body>
					</Card>
				</div>
			</div>
		</div>
	);
};
